import { projects, research } from './constants';
import { projectContent } from './projectContent';

const asset = (name) => `${process.env.PUBLIC_URL || '.'}/images/projects/${name}`;
const github = (repository, path = 'README.md') => `https://github.com/jeyapragash1/${repository}/blob/main/${path}`;

// Presentation overlays retain every original record and its original content.
// Verification scope and sources are documented in docs/PROJECT_VERIFICATION.md.
const details = {
  'Weekly Report Generator': {
    tags: ['full-stack', 'backend', 'ai'],
    status: 'Application implemented',
    description: 'Team reporting with role-based access, weekly report workflows, project tracking, and a local Ollama assistant.',
    technologies: ['React', 'TypeScript', 'Node.js', 'Express', 'PostgreSQL', 'Prisma', 'Ollama'],
    highlights: ['JWT access/refresh authentication and Admin, Manager, Team Member roles', 'Draft and submitted weekly reports with project and manager dashboards', 'Context-aware local Ollama assistant for managers and administrators'],
    architecture: ['React + Vite client → Express REST API', 'JWT and role middleware → reporting and project modules', 'Prisma → PostgreSQL; AI assistant → local Ollama'],
    verification: 'Repository documentation and file structure reviewed. This portfolio update did not execute the project or its external integrations.',
    limitations: ['PDF export and email notifications remain listed as future work.', 'The documented screenshot files were unavailable; no preview is presented as an application screenshot.'],
    sources: [{ label: 'Project documentation', url: github('weekly-report-generator') }],
  },
  'EFCE Platform': {
    tags: ['full-stack', 'backend'],
    status: 'Application implemented',
    description: 'Incident and risk analysis platform with causal graph editing, counterfactual scenarios, reporting, and persisted workflows.',
    technologies: ['Next.js', 'TypeScript', 'FastAPI', 'PostgreSQL', 'React Flow', 'Playwright'],
    highlights: ['Incident and risk workflows with typed API boundaries', 'Graph Studio and saved counterfactual scenarios', 'Documented Playwright and Storybook interaction coverage'],
    architecture: ['Next.js dashboard → typed API client', 'FastAPI domain endpoints → PostgreSQL', 'React Flow graph editor and Recharts comparisons'],
    verification: 'Repository documents Playwright, Storybook, and backend testing. Test execution and deployment were not repeated during this portfolio update.',
    limitations: ['The README retains overlapping implementation and roadmap notes; report-generation and deployment status need further confirmation.'],
    sources: [{ label: 'Project documentation', url: github('efce-platform') }],
  },
  'Deep Space Snap': {
    displayTitle: 'SpaceSnap',
    tags: ['full-stack', 'backend', 'ai', 'web'],
    status: 'Application implemented',
    description: 'AI-assisted interior design platform with room-photo visualization, style recommendations, designer portfolios, and consultation workflows.',
    problem: 'People need a practical way to explore interior styles and visualize changes to their own rooms.',
    solution: 'A room-photo workflow combines style quizzes, editable visualizations, AI redesign services, and designer collaboration.',
    technologies: ['React', 'Node.js', 'Express', 'MongoDB', 'Gemini', 'Hugging Face', 'Stripe'],
    highlights: ['Style quiz and room-image analysis', 'Editable room visualization and image/mask redesign workflows', 'Designer portfolios, consultation booking, and role-based dashboards'],
    architecture: ['React + Vite client → Express API', 'Mongoose → MongoDB; uploaded images → Cloudinary', 'AI services provide style analysis and image generation'],
    verification: 'Current repository documentation was reviewed and corrects the former astronomy description. AI, payment, and camera integrations were not exercised.',
    limitations: ['The camera/image-masking workflow is documented as AR Preview; no spatial AR validation is claimed.', 'Live service behavior and individual contribution scope remain unverified.'],
    sources: [{ label: 'SpaceSnap documentation', url: github('Deep_spaceSnap') }],
  },
  'Smart Citizen Platform': {
    displayTitle: 'Smart Citizen LK',
    tags: ['full-stack', 'web'],
    status: 'Frontend implemented',
    description: 'Sri Lankan citizen-services frontend with authentication flows, service interfaces, document verification, and officer dashboards.',
    technologies: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS'],
    highlights: ['Citizen services and document-verification interfaces', 'Registration, login, and OTP-verification flows', 'Admin and GS officer dashboards and marketplace UI'],
    architecture: ['Next.js App Router frontend → backend API', 'Modular citizen-service and dashboard interfaces'],
    verification: 'Frontend repository and documentation reviewed. End-to-end citizen-service transactions and government integrations were not verified.',
    limitations: ['Full functionality requires the companion backend.', 'The presence of a chatbot interface does not establish verified AI-provider behavior.'],
    sources: [{ label: 'Frontend documentation', url: github('Smart-Citizen') }, { label: 'Backend repository', url: 'https://github.com/jeyapragash1/Smart-Citizen-backend' }],
  },
};

const tags = {
  'AI AutoWorker': ['ai', 'backend', 'full-stack'],
  'EventFlow SaaS': ['backend'],
  'Amazon Sales Analyze': [],
  'Low-Code Workflow Automation Platform': ['ai', 'web'],
  'TDC Matchmaker Dashboard': ['web'],
  'GlobalTNA Service Request Board': ['web'],
  'Hiring Full-Stack Todo': ['full-stack', 'backend', 'web'],
  'Smart Management System': ['web'],
  'EventLanka': ['full-stack', 'web'],
  'Home Security System': ['full-stack', 'web'],
  'IAS Blog Backend': ['backend'],
  'IEEE IAS Chapter': ['web'],
  'Hospital Appointment System': ['backend'],
  'Enhanced Hospital System': ['backend'],
  'Voting System Using JSP': ['web'],
  'Sri Lankan Job Portal': ['web'],
  'On-Vote': [],
};

const additions = [
  {
    title: 'AI-Powered GitHub Code Review Assistant',
    category: 'Developer Tool', tags: ['backend', 'full-stack'],
    status: 'Review foundation implemented',
    description: 'GitHub review platform with signed webhook ingestion, durable review jobs, static analysis, and a repository dashboard.',
    problem: 'Pull-request analysis needs reliable ingestion, repeatable findings, and clear review status.',
    solution: 'A FastAPI backend persists GitHub data and durable jobs, while a Next.js dashboard exposes scoped repository and review workflows.',
    technologies: ['FastAPI', 'Python', 'Next.js', 'TypeScript', 'PostgreSQL', 'SQLAlchemy', 'GitHub API'],
    highlights: ['HMAC-verified webhook ingestion and delivery deduplication', 'Durable jobs with retries, leases, and commit-SHA deduplication', 'Ruff, Bandit, and AST-based static analysis with scoped dashboards'],
    architecture: ['GitHub REST API / signed webhooks → FastAPI', 'PostgreSQL outbox and workers → static analyzers → persisted findings', 'Authenticated Next.js dashboard → scoped backend reads'],
    challenges: ['Idempotent webhook ingestion and review jobs for the same commit', 'Recovering stale workers without duplicating completed reviews', 'Restricting repository access and analyzer execution'],
    verification: 'Local source and the implementation report were reviewed. Backend tests cover webhook security, worker behavior, job controls, and scoped reads; they were not rerun here.',
    limitations: ['Generative-AI review and GitHub comment publication are not established by the inspected implementation.', 'The implementation report leaves live App synchronization and real webhook delivery awaiting configuration.'],
    githubLink: 'https://github.com/jeyapragash1/ai-code-review-assistant',
    sources: [{ label: 'Repository', url: 'https://github.com/jeyapragash1/ai-code-review-assistant' }],
  },
  {
    title: 'Adaptive Eye-Gaze Communication System',
    category: 'Research', tags: ['research'], status: 'Research project',
    description: research[0].desc,
    problem: 'Locked-in Syndrome can prevent speech and limb movement, creating barriers to communication and computer interaction.',
    solution: 'Research into adaptive webcam-based eye-gaze communication and control with accessible interaction.',
    technologies: ['Computer Vision', 'MediaPipe', 'Eye Tracking', 'Adaptive Calibration'],
    highlights: research[0].focus,
    limitations: ['Research implementation, evaluation results, screenshots, and repository/demo links require confirmation.'],
    githubLink: '', sources: [],
  },
  {
    title: 'BizPilot AI', category: 'SaaS', tags: ['backend', 'full-stack', 'ai'], status: 'In development',
    description: 'Business operations platform combining organization, project, task, and AI-copilot workflows in a TypeScript monorepo.',
    problem: 'Teams manage business data and operational workflows across disconnected tools.',
    solution: 'A modular business workspace with shared API contracts, organization-aware services, and an AI-copilot module.',
    technologies: ['React', 'TypeScript', 'Node.js', 'Express', 'PostgreSQL', 'Prisma', 'Redis'],
    highlights: ['Authentication, refresh-token rotation, and organization-aware services', 'Modular backend with shared types and Prisma persistence', 'AI-copilot routes with permission checks and rate limiting'],
    architecture: ['React + Vite client → Express modular monolith', 'Prisma → PostgreSQL; Redis supports rate limiting', 'Shared TypeScript contracts across monorepo packages'],
    verification: 'Local application modules, package manifests, and test documentation reviewed. The public milestone file is older than the local implementation; no production-readiness claim is made.',
    limitations: ['Live AI-provider behavior and production deployment were not verified.', 'UI reference designs are not presented as application screenshots.'],
    githubLink: 'https://github.com/BizPilotLabs/bizpilot-ai',
    sources: [{ label: 'Repository', url: 'https://github.com/BizPilotLabs/bizpilot-ai' }],
  },
  {
    title: 'Project & Team Task Management Platform', category: 'Full Stack', tags: ['full-stack', 'backend', 'web'], status: 'Application implemented',
    description: 'Role-aware project and task management with team assignment, comments, dashboards, reports, and a decoupled Laravel API.',
    problem: 'Project teams need clear ownership, authorized task workflows, and visibility across roles.',
    solution: 'A Next.js frontend and Laravel REST API separate interface concerns from authentication, authorization, and project persistence.',
    technologies: ['Next.js', 'TypeScript', 'Laravel', 'PHP', 'PostgreSQL', 'Sanctum'],
    highlights: ['Administrator, Project Manager, and Team Member authorization', 'Projects, memberships, tasks, comments, and workload reports', 'Documented backend feature tests and frontend quality checks'],
    architecture: ['Next.js frontend → Laravel /api/v1 REST API', 'Sanctum + Policies + Spatie Permission → authorized workflows', 'Eloquent ORM → PostgreSQL'],
    contributions: ['Repository lists Kisho Jeyapragash as a contributor; individual module ownership is not separately documented.'],
    verification: 'Repository documentation and published screenshots reviewed. The README reports backend tests and frontend checks passing; this portfolio update did not rerun them.',
    limitations: ['Notifications, file attachments, PDF/Excel export, and real-time updates remain documented future work.'],
    image: asset('task-dashboard.png'), imageVerified: true,
    screenshots: [{ src: asset('task-dashboard.png'), alt: 'Administrator dashboard with task status and workload overview' }, { src: asset('task-mobile.png'), alt: 'Task-management dashboard on a mobile screen' }],
    githubLink: 'https://github.com/jeyapragash1/project-team-task-management-platform',
    sources: [{ label: 'Project documentation', url: github('project-team-task-management-platform') }],
  },
];

const screenshots = {
  "EventLanka": "project-1.png",
  "Home Security System": "project-2.png",
  "Sri Lankan Job Portal": "project-3.png",
  "Hospital Appointment System": "project-4.png",
  "Enhanced Hospital System": "project-5.png",
  "On-Vote": "project-8.png"
};
const screenshotFor = title => screenshots[title] ? {
  image: `${process.env.PUBLIC_URL || '.'}/images/${screenshots[title]}`, imageVerified: true,
  screenshots: [{ src: `${process.env.PUBLIC_URL || '.'}/images/${screenshots[title]}`, alt: `${title} application screenshot` }],
} : {};

const priority = [
  'AI-Powered GitHub Code Review Assistant', 'Adaptive Eye-Gaze Communication System', 'BizPilot AI',
  'Deep Space Snap', 'Smart Citizen Platform', 'EFCE Platform', 'Weekly Report Generator',
  'Project & Team Task Management Platform',
];

export const projectCatalog = [...projects, ...additions].map(project => ({
  original: project,
  ...project,
  tags: project.tags || tags[project.title] || [],
  ...details[project.title],
  ...projectContent[project.title],
  ...screenshotFor(project.title),
  isFeatured: priority.includes(project.title),
})).sort((a, b) => {
  const rank = title => priority.includes(title) ? priority.indexOf(title) : priority.length;
  return rank(a.title) - rank(b.title);
});
